import { Box, Typography, Button, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AutocompleteInput, ReferenceInput, SelectInput, useListContext, required, useDataProvider, useNotify, ImageField, useTranslate } from 'react-admin';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import { Link } from 'react-router-dom';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';
import { ProductProgressBar } from '@/components/analytics/product/ProductAnalyticsInfo.jsx';
import PropTypes from 'prop-types';
import { rankingTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { calcDaysFromSeriesData } from '@/components/analytics/PeriodFilters.jsx';
import { NoImage } from '@/components/global/fields/NoImage';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { buildDateRangeFilterUrl } from '@/helpers/filters/buildDateRangeFilterUrl.js';
import { buildListFilterUrl } from '@/helpers/filters/buildListFilterUrl.js';
import { createFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { useTagNameSource } from '@/i18n/hooks.js';

const Title = () => {
    const translate = useTranslate();
    return <Typography variant='h6'>{translate('analytics.product.ranking')}</Typography>;
};

const StyledElement = styled(Box)(() => ({
    ...analyticsStyles.element,
}));

const Element = ({ item, total, days }) => {
    const translate = useTranslate();
    const percentage = (item.current / total) * 100;
    const growth = ((item.current - item.previous) / item.previous) * 100;
    const daysStr = days === 1 ? translate('analytics.common.daySingular') : translate('analytics.common.daysWithCount', { count: days });
    const transactionsFilter = { 'transactionContainers->product->id_in': [item.id] };
    const transactionsLink = buildListFilterUrl('/transaction', transactionsFilter, ['transactionContainers->product->id_in']);

    return (
        <StyledElement className='element element--product'>
            <Box className='element-header'>
                <Box className='element-left'>
                    {item.pictureUrl ? (
                        <ImageField source='pictureUrl' title={translate('resources.product.fields.productPicture')} record={item} className='element-image' />
                    ) : (
                        <Box className='element-image'>
                            <NoImage sx={{ minWidth: '44px', minHeight: '44px' }} />
                        </Box>
                    )}
                    <DrawerLink resource='product' source={item.id} action='show' relative label={<Typography variant='caption'>{item.name}</Typography>} />
                </Box>
                <Box className='element-right'>
                    <Tooltip
                        arrow
                        placement='top'
                        classes={{ tooltip: 'tooltip-white tooltip-white--small' }}
                        title={
                            <span>
                                {translate('analytics.product.collectedProductsCount')}: <br />
                                <strong>{daysStr}</strong>
                            </span>
                        }
                        PopperProps={{
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -15],
                                    },
                                },
                            ],
                        }}
                    >
                        <Link style={{ color: Palette.black, textDecoration: 'none' }} to={transactionsLink}>
                            <Typography variant='caption'>{item.current}</Typography>
                        </Link>
                        <Growth value={growth} className='growth--small' />
                    </Tooltip>
                </Box>
                {isFinite(percentage) ? (
                    <Typography variant='caption' className='percentage'>
                        {formatNumberWithComma(percentage, 1)}%
                    </Typography>
                ) : null}
            </Box>
            <ProductProgressBar percentage={percentage} />
        </StyledElement>
    );
};

const StyledProductRanking = styled(Box)(({ theme }) => ({
    padding: '40px 40px 48px',
    borderRadius: '16px',
    backgroundColor: Palette.white,
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px',
    },
    '& .ranking-header': {
        ...analyticsStyles.rankingHeader,
    },
    '& .ranking-content': {
        marginTop: theme.spacing(2),
    },
    '& .ranking-footer': {
        ...analyticsStyles.rankingFooter,
    },
    '& .element + .element': {
        marginTop: '17px',
        [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            marginTop: '22px',
        },
    },
}));

export const ProductRanking = () => {
    const translate = useTranslate();
    const defaultRankingFilters = { rankingType: 'mostPopular', tagId: null };
    const { data, filterValues } = useListContext();
    const form = useForm({ defaultValues: defaultRankingFilters });
    const { watch } = form;
    const rankingFilterValues = watch();
    const notify = useNotify();
    const nameSource = useTagNameSource();

    const dataProvider = useDataProvider();
    const filter = { ...filterValues, ...rankingFilterValues };
    const {
        data: rankingData,
        isFetching,
        error,
    } = useQuery({
        queryKey: ['product-ranking', { filter }],
        queryFn: () => dataProvider.summary('analytic-products/product-ranking', { filter }),
    });

    if (!data || data.length === 0) return null;

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    const analytic = data.find(s => s.id === 'current').data;
    const series = data.find(s => s.id === 'series').data;
    const days = calcDaysFromSeriesData(series);
    const ranking = rankingData?.data?.data ?? [];

    return (
        <StyledProductRanking>
            <Box className='ranking-header'>
                <Title />
                <Box className='ranking-header__filters'>
                    <FormProvider {...form}>
                        <SelectInput
                            source='rankingType'
                            choices={rankingTypeChoices(translate)}
                            fullWidth
                            label={false}
                            validate={required()}
                            emptyText={translate('common.all')}
                        />
                        <Box className='autocomplete-parent'>
                            <ReferenceInput source='tagId' reference='tag' perPage={250}>
                                <AutocompleteInput
                                    emptyText={translate('analytics.common.allTags')}
                                    optionText={nameSource}
                                    filterToQuery={createFilterToQuery([nameSource])}
                                    label={false}
                                    fullWidth
                                    disablePortal={true}
                                />
                            </ReferenceInput>
                        </Box>
                    </FormProvider>
                </Box>
            </Box>
            <Box className='ranking-content'>
                {isFetching ? <CircularProgress /> : ranking.map(item => <Element key={item.id} item={item} total={analytic.packagesCount} days={days} />)}
            </Box>
            <Box className='ranking-footer'>
                <Button
                    color='secondary'
                    size='medium'
                    component={Link}
                    to={buildDateRangeFilterUrl('product', filter, 'analytics.packagesCount')}
                    variant='contained'
                >
                    {translate('analytics.product.seeProductList')}
                </Button>
            </Box>
        </StyledProductRanking>
    );
};

Element.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        pictureUrl: PropTypes.string,
        previous: PropTypes.number.isRequired,
        current: PropTypes.number.isRequired,
    }).isRequired,
    total: PropTypes.number.isRequired,
    days: PropTypes.number.isRequired,
};
